// Page Me — the six device renders and where everything sits on them.
//
// Each device body is a photoreal render (public/devices/<id>.webp, generated
// once, front-on, background keyed out). The live LCD and REAL <button> keys
// are laid over it from the rectangles below — PagerDevice draws nothing of
// the body itself.
//
// Every rectangle is { x, y, w, h } in PERCENT of the image box (left, top,
// width, height), measured on the render's pixels. `aspect` is width / height
// of the image, so the box can be sized before the picture loads.
//
//   screen        the LCD glass: the <Lcd> fills it exactly
//   lcd           LCD type sizes in % of the glass height (s = small rows,
//                 l = the big body row); the text-size setting multiplies them
//   name          where the model name is printed: { x, y } is the anchor
//                 (centre unless align = 'left'), tone light | dark
//   keys[id]      the drawn key. `r` is its corner radius (CSS), `label` where
//                 its silkscreen legend is printed: 'below' | 'above' | 'left'
//                 | 'right' | 'on' (on the cap) | { x, y } (the legend's centre);
//                 `vertical` sets it on its side (a side-edge key), `tone`
//                 overrides the device's silk tone for that legend.
//   silk          light | dark — which silk token the legends use
//   led           optional: the drawn unread LED ({ x, y } centre, d = diameter
//                 in % of the width); it glows while there are unread pages
//   thumb/thumb2x the Devices-list thumbnail, 140 and 280px tall

export const DEVICE_ART = Object.freeze({
  buzz12: {
    src: '/devices/buzz12.webp',
    thumb: '/devices/buzz12@1x.webp',
    thumb2x: '/devices/buzz12@2x.webp',
    aspect: 560 / 900,
    silk: 'light',
    screen: { x: 15.36, y: 17.11, w: 70.36, h: 17.44 },
    lcd: { s: 12.5, l: 24 },
    name: { x: 9, y: 5.2, align: 'left' },
    led: { x: 79.5, y: 7, d: 3.2 },
    keys: {
      up: { x: 13, y: 45.8, w: 15, h: 16, r: '22%/16%', label: { x: 20.5, y: 64.6 } },
      down: { x: 32.5, y: 45.8, w: 15.5, h: 16, r: '22%/16%', label: { x: 40.25, y: 64.6 } },
      read: { x: 52, y: 45.8, w: 16, h: 16, r: '22%/16%', label: { x: 60, y: 64.6 } },
      reply: { x: 71.5, y: 45.8, w: 16, h: 16, r: '22%/16%', label: { x: 79.5, y: 64.6 } },
      new: { x: 14, y: 70.5, w: 20, h: 5.5, r: '999px', label: 'below' },
      lock: { x: 40, y: 70.5, w: 20, h: 5.5, r: '999px', label: 'below' },
      del: { x: 65.5, y: 70.5, w: 20, h: 5.5, r: '999px', label: 'below' },
      mode: { x: 22, y: 86, w: 24.5, h: 3.5, r: '999px', label: 'below' },
      setup: { x: 53, y: 86, w: 24.5, h: 3.5, r: '999px', label: 'below' },
    },
  },
  topaz: {
    src: '/devices/topaz.webp',
    thumb: '/devices/topaz@1x.webp',
    thumb2x: '/devices/topaz@2x.webp',
    aspect: 606 / 900,
    silk: 'light',
    screen: { x: 17.66, y: 16.11, w: 65.84, h: 15.44 },
    lcd: { s: 13, l: 26 },
    name: { x: 50, y: 8.6 },
    keys: {
      up: { x: 14.5, y: 40.5, w: 17.5, h: 12, r: '50%', label: { x: 23.25, y: 55.4 } },
      read: { x: 40.5, y: 40.5, w: 17.5, h: 12, r: '50%', label: { x: 49.25, y: 55.4 } },
      down: { x: 66.5, y: 40.5, w: 17.5, h: 12, r: '50%', label: { x: 75.25, y: 55.4 } },
      reply: { x: 13, y: 59.5, w: 14.5, h: 6, r: '999px', label: 'below' },
      new: { x: 32.5, y: 59.5, w: 15, h: 6, r: '999px', label: 'below' },
      lock: { x: 51.5, y: 59.5, w: 15, h: 6, r: '999px', label: 'below' },
      del: { x: 71, y: 59.5, w: 15, h: 6, r: '999px', label: 'below' },
      mode: { x: 22.5, y: 81, w: 25, h: 6, r: '999px', label: 'below' },
      setup: { x: 52, y: 81, w: 24, h: 6, r: '999px', label: 'below' },
    },
  },
  memo: {
    src: '/devices/memo.webp',
    thumb: '/devices/memo@1x.webp',
    thumb2x: '/devices/memo@2x.webp',
    aspect: 576 / 900,
    silk: 'dark',
    screen: { x: 17.9, y: 26.9, w: 64.5, h: 20.8 },
    lcd: { s: 12, l: 19 },
    name: { x: 50, y: 9, tone: 'light' },
    keys: {
      up: { x: 15, y: 57.5, w: 15.5, h: 9.5, r: '26%/22%', label: 'on' },
      down: { x: 33, y: 57.5, w: 14.5, h: 9.5, r: '26%/22%', label: 'on' },
      read: { x: 51, y: 57.5, w: 15, h: 9.5, r: '26%/22%', label: 'on' },
      reply: { x: 69.5, y: 57.5, w: 15, h: 9.5, r: '26%/22%', label: 'on' },
      new: { x: 15, y: 70, w: 20.5, h: 9, r: '26%/30%', label: 'on' },
      lock: { x: 39.5, y: 70, w: 20.5, h: 9, r: '26%/30%', label: 'on' },
      del: { x: 63.5, y: 70, w: 21, h: 9, r: '26%/30%', label: 'on' },
      mode: { x: 15, y: 82, w: 32.5, h: 9, r: '16%/30%', label: 'on' },
      setup: { x: 51, y: 82, w: 33.5, h: 9, r: '16%/30%', label: 'on' },
    },
  },
  thumb950: {
    src: '/devices/thumb950.webp',
    thumb: '/devices/thumb950@1x.webp',
    thumb2x: '/devices/thumb950@2x.webp',
    aspect: 711 / 900,
    silk: 'light',
    screen: { x: 17.72, y: 14.11, w: 65.54, h: 30.56 },
    lcd: { s: 10.5, l: 15 },
    name: { x: 50, y: 47.3 },
    keys: {
      up: { x: 26.5, y: 51, w: 13, h: 4.5, r: '999px', label: 'on', tone: 'dark' },
      open: { x: 43.5, y: 51, w: 12.5, h: 4.5, r: '999px', label: 'on', tone: 'dark' },
      down: { x: 60.5, y: 51, w: 12.5, h: 4.5, r: '999px', label: 'on', tone: 'dark' },
      compose: { x: 7.5, y: 62, w: 85, h: 24.5, r: '4%', label: { x: 50, y: 59.2 } },
      mode: { x: 31, y: 89, w: 17.5, h: 4, r: '999px', label: 'below' },
      setup: { x: 51, y: 89, w: 18, h: 4, r: '999px', label: 'below' },
    },
  },
  thumb957: {
    src: '/devices/thumb957.webp',
    thumb: '/devices/thumb957@1x.webp',
    thumb2x: '/devices/thumb957@2x.webp',
    aspect: 523 / 900,
    silk: 'dark',
    screen: { x: 14.53, y: 10.11, w: 69.41, h: 53.89 },
    lcd: { s: 5.2, l: 7.5 },
    name: { x: 50, y: 4.6 },
    keys: {
      up: { x: 92, y: 22, w: 6.5, h: 7.5, r: '30% 30% 0 0', label: { x: 89.6, y: 25.8 }, tone: 'light' },
      down: { x: 92, y: 29.5, w: 6.5, h: 7.5, r: '0 0 30% 30%', label: { x: 89.6, y: 33.2 }, tone: 'light' },
      open: { x: 94.5, y: 44.5, w: 3.5, h: 5, r: '50%', label: { x: 90.2, y: 47 }, vertical: true },
      compose: { x: 7, y: 70, w: 84, h: 21.5, r: '3%', label: { x: 50, y: 67.6 } },
      mode: { x: 22.5, y: 93, w: 25, h: 4, r: '999px', label: 'left' },
      setup: { x: 50, y: 93, w: 24.5, h: 4, r: '999px', label: 'right' },
    },
  },
  track8700: {
    src: '/devices/track8700.webp',
    thumb: '/devices/track8700@1x.webp',
    thumb2x: '/devices/track8700@2x.webp',
    aspect: 500 / 900,
    silk: 'light',
    screen: { x: 9.4, y: 17.56, w: 81.2, h: 35 },
    lcd: { s: 5.6, l: 8 },
    name: { x: 50, y: 9 },
    keys: {
      mode: { x: 10.5, y: 57.8, w: 13.5, h: 4.7, r: '30%/45%', label: 'above' },
      up: { x: 26, y: 57.8, w: 12, h: 4.7, r: '20%/40%', label: 'above' },
      open: { x: 45, y: 57.3, w: 10, h: 5.7, r: '50%', label: 'above' },
      down: { x: 62, y: 57.8, w: 12, h: 4.7, r: '20%/40%', label: 'above' },
      setup: { x: 76, y: 57.8, w: 14, h: 4.7, r: '30%/45%', label: 'above' },
      compose: { x: 6, y: 64.5, w: 88, h: 27, r: '6% 6% 22% 22% / 5% 5% 26% 26%', label: { x: 50, y: 93.4 } },
    },
  },
});

/** The art for a device id (BUZZ 12's for anything unknown). */
export function artFor(id) {
  return DEVICE_ART[id] || DEVICE_ART.buzz12;
}
